// pages/sapProcessMapLive.js — live data for the Process Map from the backend
// (GET /api/sap-procmap/latest, tables procmap_* — see backend/sapProcessMap.js).
// mergeLive() lays the latest snapshot figures and the notes from the database over the
// static .js files; anything the API does not return keeps the static value, so the page
// still works when the API is down or no snapshot exists yet.
import { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { LANDSCAPES as S_LANDSCAPES, SNAPSHOT as S_SNAPSHOT } from './sapProcessMapData';
import { CO_FIGS as S_CO_FIGS } from './sapProcessMapCo';
import { STD as S_STD } from './sapProcessMapStd';
import { NOTES as S_FIND, GROUP_LABEL } from './sapProcessMapNotes';
import { NOTES as S_ORGN } from './sapOrgNotes';
import { ORG as S_ORG } from './sapOrgMasterData';

// Sort key for timeline labels such as "29 Sep 2025", "Dec 2025", "Sep 2025–Jan 2026" or "2017"
// (newest first; a label without a month sorts as January of that year).
const MON = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, mei: 5, jun: 6, jul: 7, aug: 8, agu: 8, sep: 9, oct: 10, okt: 10, nov: 11, dec: 12, des: 12 };
export function whenKey(label) {
  const m = String(label || '').match(/(?:(\d{1,2})\s+)?(?:([A-Za-z]{3})[a-z]*\.?\s+)?((?:19|20)\d{2})/);
  if (!m) return 0;
  return +m[3] * 10000 + (MON[(m[2] || '').toLowerCase()] || 1) * 100 + (+m[1] || 1);
}

let cache = null;
let inflight = null;
const subs = new Set();

export function loadLive(force = false) {
  if (inflight && !force) return inflight;
  inflight = api.get('/api/sap-procmap/latest')
    .then(r => { cache = r.data; subs.forEach(f => f(cache)); return cache; })
    .catch(() => { cache = cache || { failed: true }; subs.forEach(f => f(cache)); return cache; })
    .finally(() => { inflight = null; });
  return inflight;
}

export function useProcMapLive() {
  const [d, setD] = useState(cache);
  useEffect(() => {
    subs.add(setD);
    if (!cache) loadLive();
    return () => { subs.delete(setD); };
  }, []);
  return d;
}

export const GROUP_ORDER = ['summary', 'approval', 'integr', 'fixes', 'risks'];
export { GROUP_LABEL };

const clone = o => JSON.parse(JSON.stringify(o));
const note = n => ({ en: n.text_en, id: n.text_id || n.text_en, _n: n });

const memo = new WeakMap();
export function mergeLive(live) {
  if (live && memo.has(live)) return memo.get(live);
  const LANDSCAPES = clone(S_LANDSCAPES);
  const CO_FIGS = clone(S_CO_FIGS);
  const STD = clone(S_STD);
  const FIND = clone(S_FIND);
  const ORGN = clone(S_ORGN);
  const ORG = { ...S_ORG };
  const TX = {};                          // extra EN → ID texts coming from the database
  const SNAP = {};
  const live_ = live && !live.failed ? live : null;

  for (const land of Object.keys(LANDSCAPES)) {
    SNAP[land] = { takenOn: S_SNAPSHOT.takenOn, period: S_SNAPSHOT.period, live: false };
    const L = LANDSCAPES[land];
    const lv = live_ && live_.lands && live_.lands[land];
    if (lv) {
      SNAP[land] = { takenOn: lv.snapshot.takenOn, period: lv.snapshot.period.en, periodId: lv.snapshot.period.id, id: lv.snapshot.id, live: true };
      const all = lv.figures['*'] || {};
      for (const [fk, steps] of Object.entries(all)) {
        const f = L.flows[fk]; if (!f) continue;
        if (steps._vol) { f.vol = steps._vol.en; if (steps._vol.id) TX[steps._vol.en] = steps._vol.id; }
        for (const [sk, r] of Object.entries(steps)) {
          if (sk === '_vol') continue;
          const st = f.steps.find(s => s.id === sk); if (!st) continue;
          if (r.v) { st.v = r.v.en; if (r.v.id) TX[r.v.en] = r.v.id; }
          if (r.docs) st.docs = r.docs.map(([en, n, id]) => { if (id) TX[en] = id; return [en, n]; });
        }
      }
      const co = {};
      for (const [k, v] of Object.entries(lv.figures)) if (k !== '*') co[k] = v;
      if (Object.keys(co).length) {
        // convert to the CO_FIGS shape: { flow: { step: {n, v:{en,id}, docs:[[label,n]]}, _vol:{en,id} } }
        for (const flows of Object.values(co)) for (const steps of Object.values(flows)) for (const [sk, r] of Object.entries(steps)) {
          if (sk !== '_vol' && r.docs) r.docs = r.docs.map(([en, n, id]) => { if (id) TX[en] = id; return [en, n]; });
        }
        CO_FIGS[land] = co;
      }
      if (lv.org) ORG[land] = lv.org;
    }

    // Notes: the database is authoritative for every kind it holds for this landscape.
    const kinds = new Set((live_ && live_.kinds && live_.kinds[land]) || []);
    if (!kinds.size) continue;
    const ns = (live_.notes || []).filter(n => n.land === land && n.active);
    const by = k => ns.filter(n => n.kind === k).sort((a, b) => a.sort - b.sort || a.id - b.id);
    ns.forEach(n => { if (n.text_id && n.text_id !== n.text_en) TX[n.text_en] = n.text_id; });
    if (kinds.has('fact')) {
      const f = by('fact');
      L.facts.character = f.filter(n => n.grp === 'character').map(n => n.text_en);
      L.facts.custom = f.filter(n => n.grp === 'custom').map(n => n.text_en);
    }
    if (kinds.has('step')) {
      const f = by('step');
      for (const [fk, fl] of Object.entries(L.flows)) for (const s of fl.steps) {
        const mine = f.filter(n => n.flow === fk && n.step === s.id);
        const c = mine.filter(n => n.grp === 'custom').map(n => n.text_en);
        const o = mine.filter(n => n.grp === 'notes').map(n => n.text_en);
        if (c.length) s.custom = c; else delete s.custom;
        if (o.length) s.notes = o; else delete s.notes;
      }
    }
    if (kinds.has('std_step') || kinds.has('std_diff')) {
      const ss = by('std_step'), dd = by('std_diff');
      const flows = new Set([...ss, ...dd].map(n => n.flow));
      STD[land] = STD[land] || {};
      for (const fk of flows) STD[land][fk] = {
        steps: ss.filter(n => n.flow === fk).map(n => ({ en: n.text_en, id: n.text_id || n.text_en, tc: n.tc, status: n.status, _n: n })),
        diffs: dd.filter(n => n.flow === fk).map(n => ({ status: n.status, en: n.text_en, id: n.text_id || n.text_en, _n: n })),
      };
    }
    if (kinds.has('finding') || kinds.has('timeline')) {
      const fi = by('finding');
      const keys = [...GROUP_ORDER, ...new Set(fi.map(n => n.grp).filter(g => !GROUP_ORDER.includes(g)))];
      FIND[land] = {
        groups: keys.map(k => ({ key: k, items: fi.filter(n => n.grp === k).map(note) })).filter(g => g.items.length),
        timeline: by('timeline').sort((a, b) => whenKey(b.when_label) - whenKey(a.when_label)).map(n => [n.when_label, note(n)]),
      };
    }
    if (kinds.has('org')) {
      const o = by('org');
      const tabs = {};
      for (const t of Object.keys(S_ORGN[land] || {})) tabs[t] = [];
      o.forEach(n => { (tabs[n.grp] = tabs[n.grp] || []).push(note(n)); });
      ORGN[land] = tabs;
    }
  }
  const out = { LANDSCAPES, CO_FIGS, STD, FIND, ORGN, ORG, TX, SNAP, live: live_ };
  if (live) memo.set(live, out);
  return out;
}
